import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Bug, Plus, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import type { Animal, Pasture, Species } from "@shared/types";

// Filter sentinel for "no species selected"; cannot collide with a real
// species_id since those are UUID/slug-shaped strings.
const FILTER_ALL = "all";
const FILTER_UNKNOWN = "__unknown__";

// Display emoji per species name. We key off the species *name* rather
// than a slug so the lookup survives renames at the data layer; if the
// ranch adds a custom species (e.g. "Alpaca"), it falls through to 🐾.
const SPECIES_EMOJI: Record<string, string> = {
  Cattle: "🐄",
  Horse: "🐴",
  Goat: "🐐",
  Sheep: "🐑",
};

export function AnimalsPage() {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [pastures, setPastures] = useState<Pasture[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [speciesFilter, setSpeciesFilter] = useState<string>(FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fName, setFName] = useState("");
  // Form state holds the selected species_id (or "" for unset). The
  // selector defaults to whatever the API returns first, but we don't
  // require selection — sending null is valid.
  const [fSpeciesId, setFSpeciesId] = useState<string>("");
  const [fBreed, setFBreed] = useState("");
  const [fTag, setFTag] = useState("");
  const [fDob, setFDob] = useState("");
  const [fPasture, setFPasture] = useState("");
  const [fNotes, setFNotes] = useState("");
  // Staged image attached after the animal is created.
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchData = async () => {
    try {
      const [aData, pData, sData] = await Promise.all([
        authFetch<{ items: Animal[] }>("/api/animals"),
        authFetch<{ items: Pasture[] }>("/api/pastures"),
        authFetch<{ items: Species[] }>("/api/species"),
      ]);
      setAnimals(aData.items ?? []);
      setPastures(pData.items ?? []);
      setSpecies(sData.items ?? []);
    } catch (err: any) {
      setError(err?.message || "Failed to load");
      console.error("[Animals]", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  // species_id → Species lookup. Used by the filter and card render
  // so we don't iterate the species array on every cell.
  const speciesById = useMemo(() => {
    const map = new Map<string, Species>();
    for (const s of species) map.set(s.id, s);
    return map;
  }, [species]);

  const filtered = useMemo(() => {
    return animals.filter((a) => {
      const tag = a.tag_number ?? "";
      const ms =
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        tag.toLowerCase().includes(search.toLowerCase());
      const msp =
        speciesFilter === FILTER_ALL ||
        (speciesFilter === FILTER_UNKNOWN ? !a.species_id : a.species_id === speciesFilter);
      const mst = statusFilter === "all" || a.status === statusFilter;
      return ms && msp && mst;
    });
  }, [animals, search, speciesFilter, statusFilter]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/animals", {
        method: "POST",
        body: JSON.stringify({
          name: fName.trim(),
          // Real species_id from the dropdown; null when unset.
          species_id: fSpeciesId || null,
          breed: fBreed.trim() || null,
          tag_number: fTag.trim() || null,
          dob: fDob || null,
          pasture_id: fPasture || null,
          status: "active",
          notes: fNotes.trim() || null,
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "animal",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Animal added");
      setFName(""); setFSpeciesId(""); setFBreed(""); setFTag(""); setFDob(""); setFPasture(""); setFNotes(""); setImageFile(null);
      setDialogOpen(false);
      setLoading(true);
      fetchData();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader title="Animals" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Animals" }]}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Animal
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Animal</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Name *</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Bessie" /></div>
                <div className="space-y-2">
                  <Label>Species</Label>
                  <Select value={fSpeciesId} onValueChange={setFSpeciesId}>
                    <SelectTrigger><SelectValue placeholder="Select species..." /></SelectTrigger>
                    <SelectContent>
                      {species.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Breed</Label><Input value={fBreed} onChange={(e) => setFBreed(e.target.value)} placeholder="Angus" /></div>
                <div className="space-y-2"><Label>Tag #</Label><Input value={fTag} onChange={(e) => setFTag(e.target.value)} placeholder="A-001" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Birth Date</Label><Input type="date" value={fDob} onChange={(e) => setFDob(e.target.value)} /></div>
                <div className="space-y-2">
                  <Label>Pasture</Label>
                  <Select value={fPasture} onValueChange={setFPasture}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {pastures.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                {submitting ? "Adding..." : "Add Animal"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name or tag..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Species" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}>All Species</SelectItem>
            {species.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
            <SelectItem value={FILTER_UNKNOWN}>Unspecified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
            <SelectItem value="deceased">Deceased</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
            <SelectItem value="quarantined">Quarantined</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <CardGridSkeleton count={6} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Bug}
          title="No animals found"
          description={search || speciesFilter !== FILTER_ALL || statusFilter !== "all" ? "Try adjusting your filters." : "Add your first animal to get started."}
          actionLabel={!search && speciesFilter === FILTER_ALL && statusFilter === "all" ? "Add Animal" : undefined}
          onAction={!search && speciesFilter === FILTER_ALL && statusFilter === "all" ? () => setDialogOpen(true) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => {
            const sp = a.species_id ? speciesById.get(a.species_id) : undefined;
            const speciesName = sp?.name ?? "Unspecified";
            const emoji = sp ? (SPECIES_EMOJI[sp.name] ?? "🐾") : "🐾";
            return (
              <Link key={a.id} to={`/animals/${a.id}`}>
                <Card className="p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer group">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg group-hover:scale-110 transition-transform inline-block">{emoji}</span>
                      <h3 className="font-semibold text-foreground">{a.name}</h3>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="text-sm text-muted-foreground">{speciesName} • {a.breed || "Unknown breed"}</p>
                  <p className="text-sm text-muted-foreground mt-1">Tag: {a.tag_number || "—"}</p>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
